import {ADataGrid, ADataGridColumn, ADataGridFilter, ASectionTitle, PagedSearchParams} from "@atiautomacao/ati-ui-library";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {extractFiltersFromColumns} from "../../../../Utils/DataUitils";
import {useSnackbar} from "notistack";
import {Box, Chip} from "@mui/material";
import {TeleObjectMapItemSearchReactQuery} from "../PowerStationReactQuery";

interface Props {
    powerStationId: number
}

interface AssociatedTracker {
    trackerTeleObjectId?: number;
    trackerEquipmentId?: number;
    trackerEquipmentName?: string;
    trackerTeleObjectConfigName?: string;
    teleObjectMapAssociatedTrackerName?: string;
}

interface TeleObjectMapItemInverterTrackersRow {
    inverterEquipmentId?: number;
    inverterEquipmentName?: string;
    skidId?: number | null;
    skidName?: string | null;
    associatedTrackers?: Array<AssociatedTracker>;
}

interface GridRow {
    inverter?: string;
    associatedTrackersNode?: React.ReactNode;
    associatedTrackers?: Array<AssociatedTracker>;
}

const initColumnsData = [
    {
        name: 'inverterEquipmentName',
        label: 'Inversor',
        align: 'left',
        minWidth: 1,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'inverterEquipmentName', condition: 'contains', value: '', sort: 'asc'}
    },
     {
        name: 'skidName',
        label: 'Skid',
        align: 'left',
        minWidth: 1,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'skidName', condition: 'contains', value: '', sort: 'asc'}
    },
    {
        name: 'associatedTrackersNode',
        label: 'Trackers associados',
        align: 'left',
        minWidth: 200,
        breakpoints: {
            xs: true,
            sm: true,
            md: true,
            lg: true,
            xl: true
        },
        filter: {name: 'associatedTrackersNode', condition: 'contains', value: null, sort: null}
    },
] as ADataGridColumn[];

export const PowerStationFormEditTeleObjectMapInvertersTrackersPage = ({
    powerStationId
}: Props) => {
    const {enqueueSnackbar} = useSnackbar();
    const columns = useMemo(() => initColumnsData, []);
    const [pagedSearchParams, setPagedSearchParams] = useState(new PagedSearchParams(extractFiltersFromColumns(initColumnsData), 0, 10));

    const teleObjectMapItemGrid = TeleObjectMapItemSearchReactQuery();
    const [rows, setRows] = useState<Array<GridRow>>([]);
    const [totalOfRecords, setTotalOfRecords] = useState<number>(0);
    const [loading, setLoading] = useState(false);

    const handleOnFiltersChange = useCallback((dataGridFilters: Array<ADataGridFilter>, page: number, pageSize: number) => {
        const newPageParams = new PagedSearchParams(dataGridFilters, page, pageSize);
        setPagedSearchParams(newPageParams);
    }, []);

    const processSearchResponse = (response: any) => {
        const result = response;
        const apiRows: Array<TeleObjectMapItemInverterTrackersRow> = result?.data ?? [];

        setTotalOfRecords(result?.totalOfRecords ?? 0);

        const mappedRows: GridRow[] = apiRows.map((item) => {
            const associatedTrackers = item?.associatedTrackers ?? [];

            return {
                inverterEquipmentName: item?.inverterEquipmentName ?? '-',
                skidName: item?.skidName,
                associatedTrackers,
                associatedTrackersNode: (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            maxHeight: 90,
                            overflowY: 'auto',
                            pt: '10px',
                            '&::-webkit-scrollbar': {width: 8},
                            '&::-webkit-scrollbar-track': {background: 'transparent'},
                            '&::-webkit-scrollbar-thumb': {backgroundColor: '#BDBDBD', borderRadius: 6},
                            '&::-webkit-scrollbar-button': {display: 'none'},
                            '&::-webkit-scrollbar-arrow': {background: 'transparent'},
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#BDBDBD transparent',
                        }}
                    >
                        {associatedTrackers.map((tracker) => {
                            const trackerLabel = tracker?.trackerTeleObjectConfigName && tracker?.trackerTeleObjectConfigName?.length > 6 ? `${tracker?.trackerTeleObjectConfigName?.substring(0, 6)}` : tracker?.trackerTeleObjectConfigName ?? '-';
                            const equipmentLabel = tracker?.trackerEquipmentName && tracker?.trackerEquipmentName?.length > 3 ? 
                            `${tracker?.trackerEquipmentName?.substring(0, 3)} ${tracker?.trackerEquipmentName?.substring(tracker.trackerEquipmentName.length-1, tracker.trackerEquipmentName.length)}` : tracker?.trackerEquipmentName ?? '-';
                            
                            const trackerConcatenatedWithEqName = `${trackerLabel} (${equipmentLabel})`;
                            return <Chip
                                key={tracker?.teleObjectMapAssociatedTrackerName ? tracker?.teleObjectMapAssociatedTrackerName : tracker?.trackerTeleObjectConfigName}
                                label={tracker?.teleObjectMapAssociatedTrackerName ? tracker?.teleObjectMapAssociatedTrackerName : tracker?.trackerTeleObjectConfigName}
                                variant="outlined"
                                color="primary"
                                size="small"
                                title={tracker?.teleObjectMapAssociatedTrackerName ? tracker?.teleObjectMapAssociatedTrackerName : tracker?.trackerTeleObjectConfigName}
                                sx={{mr: 1, mb: 1,width: '120px' }}
                            />
                        })}
                    </Box>
                )
            };
        });

        setRows(mappedRows);

        const noAssociationFound = apiRows.length === 0 || apiRows.every((row) => (row?.associatedTrackers ?? []).length === 0);
        if (noAssociationFound) {
            enqueueSnackbar("Não foram encontradas associações de Correntes a Trackers. Revisar cadastro na aba Associação Tracker/Correntes", {variant: "warning"});
        }
    };

    useEffect(() => {

        if (powerStationId == null) {
            setRows([]);
            setTotalOfRecords(0);
            return;
        }

        setLoading(true);

        teleObjectMapItemGrid.mutate({pagedSearchParams, powerStationId}, {
            onSuccess: (response: any) => {
                processSearchResponse(response);
                setLoading(false);
            },
            onError: () => {
                setLoading(false);
            }
        });
    }, [pagedSearchParams, powerStationId]);

    return (
        <Box style={{padding: 16}}>
            <ASectionTitle
                styleContainer={{paddingBottom: 24}}
                titleLabel={"Mapeamento de Inversores e Trackers"}
            />

            <ADataGrid
                hideSelection={true}
                columns={columns}
                rows={rows}
                page={pagedSearchParams.page}
                rowsPerPage={pagedSearchParams.size}
                loading={loading}
                totalOfRecords={totalOfRecords}
                onFiltersChange={handleOnFiltersChange}
                disableActionsHeaderIcons={true}
            />
        </Box>
    )
}
